"use client";

import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { getBrowserSupabaseClient } from "@wtc/supabase";
import { useContactByEmail } from "@/features/contacts/hooks";
import type { Message } from "@/lib/core/crm/types/message";
import { ACTIVE_STAGES } from "@/lib/constants/deal-stages";
import {
  ACTIVE_TICKET_STATUSES,
} from "@/lib/constants/ticket-statuses";

/**
 * Related-entities data layer for the message detail "Related" tab.
 *
 * Resolves each unique thread participant email -> contact (+ company), then
 * fetches that participant's active deals (by company), open tickets (by
 * contact or company), and open tasks (by contact). Enum-concept filters reuse
 * the shared deal-stage / ticket-status constants (ENUM_VALUE_CONSTANTS) — never
 * hardcoded status strings.
 */

export interface RelatedDeal {
  id: string;
  title: string | null;
  stage: string | null;
}

export interface RelatedTicket {
  id: string;
  title: string | null;
  status: string | null;
  priority: string | null;
}

export interface RelatedTask {
  id: string;
  title: string | null;
  status: string | null;
  priority: string | null;
  due_date: string | null;
}

export interface MessageRelatedEntities {
  deals: RelatedDeal[];
  tickets: RelatedTicket[];
  tasks: RelatedTask[];
}

interface ResolvedParticipant {
  email: string;
  contactId: string;
  companyId: string | null;
}

const ACTIVE_STAGE_SET: ReadonlySet<string> = new Set<string>(ACTIVE_STAGES);
const ACTIVE_TICKET_STATUS_SET: ReadonlySet<string> = new Set(ACTIVE_TICKET_STATUSES);

/** A deal stage that means the deal is in the active sales pipeline. */
function isActiveDealStage(stage: string | null): boolean {
  return stage != null && ACTIVE_STAGE_SET.has(stage);
}

/** A ticket status that means the ticket still needs work / a response. */
function isOpenTicketStatus(status: string | null): boolean {
  return status != null && ACTIVE_TICKET_STATUS_SET.has(status);
}

/** A task is open when it is not completed. */
function isOpenTaskStatus(status: string | null): boolean {
  return status !== "completed" && status !== "cancelled";
}

/** Collect the unique, lowercased participant emails on a message thread. */
export function collectParticipantEmails(message: Message): string[] {
  const emails: string[] = [];
  const push = (email: string | undefined): void => {
    if (!email) return;
    const normalized = email.trim().toLowerCase();
    if (normalized && !emails.includes(normalized)) {
      emails.push(normalized);
    }
  };

  push(message.sender?.email);
  for (const recipient of message.recipients ?? []) push(recipient.email);
  for (const recipient of message.ccRecipients ?? []) push(recipient.email);

  return emails;
}

/**
 * Resolve a single email to a contact's id + company id.
 * Mirrors ContactContextCard's per-email resolution (useContactByEmail).
 */
function useResolvedParticipant(email: string): {
  participant: ResolvedParticipant | null;
  isLoading: boolean;
} {
  const { data: contact, isLoading } = useContactByEmail(email);

  const participant = React.useMemo<ResolvedParticipant | null>(() => {
    const contactId = typeof contact?.id === "string" ? contact.id : null;
    if (!contactId) return null;
    const companyId =
      typeof contact?.company_id === "string" ? contact.company_id : null;
    return { email, contactId, companyId };
  }, [contact, email]);

  return { participant, isLoading };
}

interface UseMessageRelatedEntitiesResult {
  data: MessageRelatedEntities;
  isLoading: boolean;
  isResolvingContacts: boolean;
  /** True when at least one participant resolved to a known contact. */
  hasResolvedContacts: boolean;
}

const EMPTY_RESULT: MessageRelatedEntities = { deals: [], tickets: [], tasks: [] };

/** Coerce an unknown DB value to a non-empty string id, or null. */
function asId(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** Coerce an unknown DB value to a string field or null. */
function asNullableString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/**
 * Fetch active deals / open tickets / open tasks for every contact + company on
 * a message thread. Single Supabase query per entity type (batched by id sets)
 * keeps this to three queries regardless of participant count (main-01).
 */
export function useMessageRelatedEntities(
  message: Message,
): UseMessageRelatedEntitiesResult {
  const emails = React.useMemo(() => collectParticipantEmails(message), [message]);

  // Resolve up to a sensible cap of participants. The hook list length is stable
  // for a given message (emails are memoized per message id), satisfying Rules of
  // Hooks — Related tab always renders for one message at a time.
  const MAX_PARTICIPANTS = 10;
  const resolveEmails = React.useMemo(
    () => emails.slice(0, MAX_PARTICIPANTS),
    [emails],
  );

  const padded = React.useMemo(() => {
    const list = [...resolveEmails];
    while (list.length < MAX_PARTICIPANTS) list.push("");
    return list;
  }, [resolveEmails]);

  // Fixed number of hook calls (MAX_PARTICIPANTS); empty strings are disabled.
  const r0 = useResolvedParticipant(padded[0]);
  const r1 = useResolvedParticipant(padded[1]);
  const r2 = useResolvedParticipant(padded[2]);
  const r3 = useResolvedParticipant(padded[3]);
  const r4 = useResolvedParticipant(padded[4]);
  const r5 = useResolvedParticipant(padded[5]);
  const r6 = useResolvedParticipant(padded[6]);
  const r7 = useResolvedParticipant(padded[7]);
  const r8 = useResolvedParticipant(padded[8]);
  const r9 = useResolvedParticipant(padded[9]);

  const resolvers = [r0, r1, r2, r3, r4, r5, r6, r7, r8, r9];

  const isResolvingContacts = resolvers.some(
    (r, i) => padded[i] !== "" && r.isLoading,
  );

  const contactIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const r of resolvers) {
      if (r.participant) ids.add(r.participant.contactId);
    }
    return Array.from(ids).sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resolvers.map((r) => r.participant?.contactId));

  const companyIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const r of resolvers) {
      if (r.participant?.companyId) ids.add(r.participant.companyId);
    }
    return Array.from(ids).sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resolvers.map((r) => r.participant?.companyId));

  const hasResolvedContacts = contactIds.length > 0;
  const enabled = hasResolvedContacts || companyIds.length > 0;

  const { data, isLoading } = useQuery({
    queryKey: ["message-related-entities", contactIds, companyIds],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<MessageRelatedEntities> => {
      const supabase = getBrowserSupabaseClient();

      const [dealsRes, ticketsRes, tasksRes] = await Promise.all([
        // Active deals at any participant's company.
        companyIds.length > 0
          ? supabase
              .from("deals")
              .select("id, title, stage, company_id")
              .in("company_id", companyIds)
              .order("created_at", { ascending: false })
              .limit(50)
          : Promise.resolve({ data: [], error: null }),
        // Open tickets for any participant contact OR their company.
        contactIds.length > 0 || companyIds.length > 0
          ? supabase
              .from("tickets")
              .select("id, title, status, priority, contact_id, company_id")
              .or(
                [
                  contactIds.length > 0
                    ? `contact_id.in.(${contactIds.join(",")})`
                    : null,
                  companyIds.length > 0
                    ? `company_id.in.(${companyIds.join(",")})`
                    : null,
                ]
                  .filter(Boolean)
                  .join(","),
              )
              .order("created_at", { ascending: false })
              .limit(50)
          : Promise.resolve({ data: [], error: null }),
        // Open tasks for any participant contact.
        contactIds.length > 0
          ? supabase
              .from("contact_tasks")
              .select("id, title, status, priority, due_date, contact_id")
              .in("contact_id", contactIds)
              .order("due_date", { ascending: true, nullsFirst: false })
              .limit(50)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (dealsRes.error) throw new Error(dealsRes.error.message);
      if (ticketsRes.error) throw new Error(ticketsRes.error.message);
      if (tasksRes.error) throw new Error(tasksRes.error.message);

      const dealRows: unknown[] = dealsRes.data ?? [];
      const deals: RelatedDeal[] = dealRows
        .map((row): RelatedDeal => {
          const d = row as Record<string, unknown>;
          return {
            id: asId(d.id),
            title: asNullableString(d.title),
            stage: asNullableString(d.stage),
          };
        })
        .filter((d) => d.id !== "" && isActiveDealStage(d.stage));

      const ticketRows: unknown[] = ticketsRes.data ?? [];
      const seenTickets = new Set<string>();
      const tickets: RelatedTicket[] = ticketRows
        .map((row): RelatedTicket => {
          const t = row as Record<string, unknown>;
          return {
            id: asId(t.id),
            title: asNullableString(t.title),
            status: asNullableString(t.status),
            priority: asNullableString(t.priority),
          };
        })
        .filter((t) => {
          if (t.id === "" || !isOpenTicketStatus(t.status)) return false;
          if (seenTickets.has(t.id)) return false;
          seenTickets.add(t.id);
          return true;
        });

      const taskRows: unknown[] = tasksRes.data ?? [];
      const tasks: RelatedTask[] = taskRows
        .map((row): RelatedTask => {
          const t = row as Record<string, unknown>;
          return {
            id: asId(t.id),
            title: asNullableString(t.title),
            status: asNullableString(t.status),
            priority: asNullableString(t.priority),
            due_date: asNullableString(t.due_date),
          };
        })
        .filter((t) => t.id !== "" && isOpenTaskStatus(t.status));

      return { deals, tickets, tasks };
    },
  });

  return {
    data: data ?? EMPTY_RESULT,
    isLoading: enabled ? isLoading : false,
    isResolvingContacts,
    hasResolvedContacts,
  };
}
